import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";

const navLinks = [
  { to: "/", label: "Home", end: true },
  { to: "/shop", label: "Shop" },
  { to: "/boxes", label: "Pick Ah Box" },
  { to: "/build", label: "Build Yuh Own Box" },
  { to: "/party", label: "Party Box" },
  { to: "/blog", label: "Blog" },
];

export default function Navbar() {
  const { count } = useCart();
  const { user } = useAuth();
  const { currency, toggle } = useCurrency();
  const [open, setOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-zinc-200" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-1.5 sm:gap-3 min-w-0" data-testid="nav-logo">
          <img src="/brand/fruit-blossoms.png" alt="Fruit Blossoms" className="h-9 sm:h-12 w-auto flex-shrink-0" />
          <span className="w-px h-7 sm:h-8 bg-zinc-200 flex-shrink-0" />
          <img src="/brand/fruit-popz.png" alt="Fruit Popz" className="h-8 sm:h-10 w-auto flex-shrink-0" />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[#F74B64]" : "text-zinc-700 hover:text-zinc-900"}`
              }>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
          <button
            onClick={toggle}
            className="text-[11px] sm:text-xs font-semibold px-2.5 py-1.5 rounded-full border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors inline-flex items-center gap-1"
            title={`Showing prices in ${currency}. Click to switch.`}
            data-testid="currency-toggle"
          >
            <span className={currency === "JMD" ? "text-[#F74B64]" : "text-zinc-400"}>JMD</span>
            <span className="text-zinc-300">|</span>
            <span className={currency === "USD" ? "text-[#F74B64]" : "text-zinc-400"}>USD</span>
          </button>
          {(user && ["admin", "super_admin", "staff"].includes(user.role)) && (
            <Link to="/admin" className="hidden sm:inline-flex text-xs font-semibold px-3 py-1.5 rounded-full bg-zinc-900 text-white hover:bg-zinc-700" data-testid="nav-admin-link">
              Backend
            </Link>
          )}
          {user && user.role === "customer" && (
            <Link to="/account" className="hidden sm:inline-flex items-center gap-2 text-sm text-zinc-700 hover:text-zinc-900" data-testid="nav-account-link">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : user.avatar_url}
                  alt={user.name}
                  className="w-7 h-7 rounded-full object-cover"
                />
              ) : (
                <User size={16} />
              )}
              My account
            </Link>
          )}
          {!user && (
            <Link to="/login?mode=register" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors" data-testid="nav-account-cta">
              <User size={14} /> Create account
            </Link>
          )}
          <Link to="/cart" className="relative p-2 rounded-full hover:bg-zinc-100" data-testid="nav-cart-link">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#F74B64", color: "white" }} data-testid="cart-count-badge">
                {count}
              </span>
            )}
          </Link>
          <button className="md:hidden p-2 rounded-full hover:bg-zinc-100" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-zinc-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
                className="text-base font-medium py-2" data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                {l.label}
              </NavLink>
            ))}
            {!user && <NavLink to="/login" onClick={() => setOpen(false)} className="text-base font-medium py-2">Login</NavLink>}
            {user && user.role === "customer" && <NavLink to="/account" onClick={() => setOpen(false)} className="text-base font-medium py-2">My account</NavLink>}
            {user && ["admin", "super_admin", "staff"].includes(user.role) && <NavLink to="/admin" onClick={() => setOpen(false)} className="text-base font-medium py-2">Backend</NavLink>}
          </div>
        </div>
      )}
    </nav>
  );
}
